'use client';

import { BodyPrimary } from '@liveauctioneers/hammer-ui-core/text';
import { CheckboxGroup, CheckboxOption } from '@liveauctioneers/hammer-ui-core/checkbox';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { ToggleSwitch } from '@liveauctioneers/hammer-ui-core/toggleSwitch';

import type { MasterToggleCheckboxGroupProps } from './types';

const DEFAULT_TEST_ID = 'master-toggle-checkbox-group';

export const MasterToggleCheckboxGroup = <T extends string>({
    areOptionsDisabled = false,
    className,
    'data-testid': dataTestId = DEFAULT_TEST_ID,
    isMasterDisabled = false,
    isMasterEnabled,
    masterLabel,
    onMasterToggle,
    onOptionToggle,
    options,
    selectedValues,
}: MasterToggleCheckboxGroupProps<T>) => (
    <div
        className={cn('flex flex-col gap-sm', className)}
        data-testid={dataTestId}
    >
        <div className="flex items-center justify-between gap-sm">
            <BodyPrimary>{masterLabel}</BodyPrimary>
            <ToggleSwitch
                aria-label={masterLabel}
                data-testid={`${dataTestId}-master`}
                disabled={isMasterDisabled}
                isToggled={isMasterEnabled}
                onToggleChange={(isToggled) => {
                    onMasterToggle(Boolean(isToggled));
                }}
                small
            />
        </div>
        <CheckboxGroup className="grid grid-cols-2 gap-xs p-0">
            {options.map((option) => (
                <CheckboxOption
                    checked={selectedValues.includes(option.value)}
                    data-testid={option['data-testid'] ?? `${dataTestId}-option-${option.value}`}
                    disabled={!isMasterEnabled || areOptionsDisabled}
                    key={option.value}
                    onChange={(event) => {
                        onOptionToggle(option.value, event.currentTarget.checked);
                    }}
                >
                    {option.label}
                </CheckboxOption>
            ))}
        </CheckboxGroup>
    </div>
);

MasterToggleCheckboxGroup.displayName = 'MasterToggleCheckboxGroup';
